/**
 * Billing status snapshot display mapping (GRMSBGENERAL-1462).
 * Maps raw B2BBillingStatus enum values from OrderService to human-readable labels.
 * Keys must stay in sync with the B2BBillingStatus enum in the OrderService
 * subgraph (see supergraph.graphql). Any value the backend can emit needs an
 * entry here, otherwise the column falls back to the raw SCREAMING_SNAKE_CASE
 * value and the filter offers no option for it.
 *
 * NOTE: This is a separate source of truth from the `BillingStatus` enum in
 * Activity/Details/InvoicesTab/InvoicesTab.tsx, which describes invoice-level
 * billing_status strings (a different data source with different labels, e.g.
 * 'Bill Immediately - Billing Console'). If you change a label here, check
 * whether that enum needs a matching update so the two do not silently diverge.
 */
export const BILLING_STATUS_SNAPSHOT_DISPLAY: Record<string, string> = {
    TO_BE_BILLED: 'To be billed',
    BILL_IMMEDIATELY: 'Bill Immediately',
    BILLED_VIA_CLIPPER_OCB: 'Billed via Clipper OCB',
    TO_BE_ACCRUED: 'To be accrued',
    ACCRUED: 'Accrued',
    BILLED: 'Billed',
    DRAFT: 'Draft',
    TO_BE_BILLED_MANUALLY: 'To be billed - manually',
    BILLING_IN_PROGRESS: 'Billing in Progress',
    BILLING_FAILED: 'Billing failed',
    NOT_APPLICABLE: 'Not applicable',
    REBILL: 'Rebill',
    CREDIT_MEMO_ISSUED: 'Credit Memo issued',
};

/**
 * Reverse mapping: display label → raw B2BBillingStatus enum.
 * Used by the Activity List filter to convert user-selected labels back to
 * the raw values stored in OpenSearch before sending the query to the backend.
 */
export const BILLING_STATUS_LABEL_TO_RAW: Record<string, string> =
    Object.fromEntries(
        Object.entries(BILLING_STATUS_SNAPSHOT_DISPLAY).map(([raw, label]) => [
            label,
            raw,
        ]),
    );

/**
 * Convert a filter chip's display label back to the raw B2BBillingStatus enum
 * for persistence/query. Mirrors the raw->label rehydration fallback: an
 * unmapped status is displayed as its raw value, so an unresolved label falls
 * back to the value as-is. This keeps the visible chip and the sent query in
 * sync instead of silently dropping a filter the user can still see.
 */
export const billingStatusLabelToRaw = (value: string): string =>
    BILLING_STATUS_LABEL_TO_RAW[value] ?? value;
